import React, { useState, useEffect, useCallback } from 'react';

function RollupLeaderboardManagement() {
    // State variables
    const [events, setEvents] = useState([]);
    const [competitions, setCompetitions] = useState([]);
    const [selectedEventID, setSelectedEventID] = useState('');
    const [leaderboardName, setLeaderboardName] = useState('');
    const [selectedCompetitions, setSelectedCompetitions] = useState([]);
    const [topNCompetitors, setTopNCompetitors] = useState('9999');
    const [groupedBy, setGroupedBy] = useState('Individual');
    const [weightingMethod, setWeightingMethod] = useState('points');
    const [rollupMethod, setRollupMethod] = useState('Rank');
    const [tieBreaker, setTieBreaker] = useState('');
    const [rollupLeaderboards, setRollupLeaderboards] = useState([]);
    const [editing, setEditing] = useState(false);
    const [currentLeaderboardID, setCurrentLeaderboardID] = useState(null);
    const [isEnabled, setIsEnabled] = useState(true);
    const [refreshInterval, setRefreshInterval] = useState(120);
    const [showCompetitorID, setShowCompetitorID] = useState(true);
    const [showCompetitorName, setShowCompetitorName] = useState(true);
    const [showOrganization, setShowOrganization] = useState(true);
    const [showCompetitorState, setShowCompetitorState] = useState(false);
    const [showDivision, setShowDivision] = useState(true);
    const [showRollupPoints, setShowRollupPoints] = useState(false);
    const [showCompetitionRanks, setShowCompetitionRanks] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [openHelpId, setOpenHelpId] = useState(null);

    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

    // Top N competitors options
    const topNOptions = [1, 2, 3, 5, 10, 100, 9999];
    
    // Grouping options
    const groupingOptions = ['Individual', 'Organization'];

    const selectedCompetitionDetails = selectedCompetitions
        .map((selected) => competitions.find((competition) => competition.CompetitionID === selected.competitionID))
        .filter(Boolean);

    const scoreDirectionSet = new Set(
        selectedCompetitionDetails
            .map((competition) => (competition.HighScoreWins ? 'high' : 'low'))
    );

    const hasMixedScoreDirections = scoreDirectionSet.size > 1;

    const toggleHelp = (key) => {
        setOpenHelpId(prev => (prev === key ? null : key));
    };

    const openModal = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditing(false);
        setCurrentLeaderboardID(null);
        setLeaderboardName('');
        setSelectedCompetitions([]);
        setTieBreaker('');
        setGroupedBy('Individual');
        setTopNCompetitors('9999');
        setWeightingMethod('points');
        setRollupMethod('Rank');
        setRefreshInterval(120);
        setIsEnabled(true);
        applyGroupVisibilityDefaults('Individual');
        setShowCompetitionRanks(false);
    };

    const applyGroupVisibilityDefaults = (group) => {
        const isIndividual = group === 'Individual';
        setShowCompetitorID(isIndividual);
        setShowCompetitorName(isIndividual);
        setShowOrganization(true);
        setShowCompetitorState(false);
        setShowDivision(isIndividual);
        setShowRollupPoints(false);
        setShowCompetitionRanks(true);
        if (!isIndividual) {
            setShowCompetitorID(false);
            setShowCompetitorName(false);
            setShowDivision(false);
        }
    };

    useEffect(() => {
        if (hasMixedScoreDirections && rollupMethod === 'Average') {
            setRollupMethod('Rank');
        }
    }, [hasMixedScoreDirections, rollupMethod]);
    
    // Fetch competitions when event is selected
    const fetchCompetitions = useCallback(async (eventID) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getCompetitions?EventID=${eventID}`);
            const data = await response.json();
            setCompetitions(Array.isArray(data) ? data : []);
            if (!editing) {
                setSelectedCompetitions([]);
            }
        } catch (error) {
            console.error('Error fetching competitions:', error);
        }
    }, [API_BASE_URL]); // Removed editing from dependencies to break circular dependency

    // Fetch rollup leaderboards for the selected event
    const fetchRollupLeaderboards = useCallback(async () => {
        if (!selectedEventID) return;
        try {
            const response = await fetch(`${API_BASE_URL}/getAllRollupLeaderboards?EventID=${selectedEventID}`);
            const data = await response.json();
            setRollupLeaderboards(data);
        } catch (error) {
            console.error('Error fetching rollup leaderboards:', error);
        }
    }, [API_BASE_URL, selectedEventID]);

    // Fetch events on component load
    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/getAllEvents`);
                const data = await response.json();
                const enabledEvents = data.filter(event => event.Enabled);
                setEvents(enabledEvents);
                if (enabledEvents.length > 0) {
                    setSelectedEventID(enabledEvents[0].EventID);
                    fetchCompetitions(enabledEvents[0].EventID);
                }
            } catch (error) {
                console.error('Error fetching events:', error);
            }
        };

        fetchEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [API_BASE_URL]); // Removed fetchCompetitions from dependencies

    // Fetch rollup leaderboards when event is selected
    useEffect(() => {
        fetchRollupLeaderboards();
    }, [fetchRollupLeaderboards]);

    // Handle event selection change
    const handleEventChange = (e) => {
        const eventID = Number(e.target.value);
        setSelectedEventID(eventID);
        fetchCompetitions(eventID);
        
        // Clear form if switching events while editing
        if (editing) {
            handleCancelEdit();
        }
    };

    // Handle competition checkbox change
    const handleCompetitionChange = (competitionId, weight) => {
        setSelectedCompetitions(prev => {
            const existing = prev.find(c => c.competitionID === competitionId);
            if (existing) {
                return prev.map(c => c.competitionID === competitionId ? { ...c, weight } : c);
            } else {
                return [...prev, { competitionID: competitionId, weight }];
            }
        });
    };

    // Handle grouping change
    const handleGroupingChange = (e) => {
        const group = e.target.value;
        setGroupedBy(group);
        // If Individual grouping is selected, topN must be All
        if (group === 'Individual') {
            setTopNCompetitors(9999);
        }
        applyGroupVisibilityDefaults(group);
    };

    // Handle delete rollup leaderboard
    const handleDelete = async (rollupLeaderboardID) => {
        if (!window.confirm('Are you sure you want to delete this rollup leaderboard?')) return;
        try {
            const response = await fetch(`${API_BASE_URL}/deleteRollupLeaderboard?RollupLeaderboardID=${rollupLeaderboardID}`, {
                method: 'DELETE',
            });
            const result = await response.json();
            if (response.ok) {
                alert(result.message);
                setRollupLeaderboards(prev => prev.filter(lb => lb.RollupLeaderboardID !== rollupLeaderboardID));
            } else {
                alert(result.error || 'Failed to delete rollup leaderboard.');
            }
        } catch (error) {
            console.error('Error deleting rollup leaderboard:', error);
        }
    };

    // Handle save rollup leaderboard
    const handleSave = async () => {
        if (!leaderboardName || !selectedEventID || !groupedBy || !weightingMethod || !rollupMethod || !selectedCompetitions.length) {
            alert('Please fill out all required fields.');
            return;
        }

        if (hasMixedScoreDirections && rollupMethod === 'Average') {
            alert('Average rollups are not allowed when mixed high-score and low-score competitions are selected. Use Rank instead.');
            return;
        }
    
        const topNValue = parseInt(topNCompetitors, 10);
    
        const payload = {
            RollupLeaderboardName: leaderboardName,
            EventID: selectedEventID,
            GroupBy: groupedBy,
            TopN: topNValue,
            WeightingMethod: weightingMethod,
            RollupMethod: rollupMethod,
            TieBreakingCompetitionID: tieBreaker,
            RefreshInterval: refreshInterval,
            Enabled: isEnabled,
            ShowCompetitorID: showCompetitorID,
            ShowCompetitorName: showCompetitorName,
            ShowOrganization: showOrganization,
            ShowCompetitorState: showCompetitorState,
            ShowDivision: showDivision,
            ShowRollupPoints: showRollupPoints,
            ShowCompetitionRanks: showCompetitionRanks,
            CompetitionIDs: selectedCompetitions,
        };
    
        try {
            const endpoint = editing 
                ? `${API_BASE_URL}/updateRollupLeaderboard` 
                : `${API_BASE_URL}/createRollupLeaderboard`;
            
            const method = editing ? 'PUT' : 'POST';
            
            // Add the RollupLeaderboardID for updates
            if (editing) {
                payload.RollupLeaderboardID = currentLeaderboardID;
            }
            
            const response = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
    
            const result = await response.json();
            if (response.ok) {
                alert(result.message);
                closeModal();
                fetchRollupLeaderboards(); // Refresh the leaderboard list
            } else {
                alert(result.error || `Failed to ${editing ? 'update' : 'save'} rollup leaderboard.`);
            }
        } catch (error) {
            console.error(`Error ${editing ? 'updating' : 'saving'} rollup leaderboard:`, error);
        }
    };

    // Handle edit rollup leaderboard
    const handleEdit = async (leaderboard) => {
        setEditing(true);
        setCurrentLeaderboardID(leaderboard.RollupLeaderboardID);
    
        // Set form values from the leaderboard
        setLeaderboardName(leaderboard.RollupLeaderboardName);
        setGroupedBy(leaderboard.GroupBy);
        setTopNCompetitors(leaderboard.TopN);
        setWeightingMethod(leaderboard.WeightingMethod);
        setRollupMethod(leaderboard.RollupMethod || 'Rank');
        setRefreshInterval(leaderboard.RefreshInterval || 120);
        setIsEnabled(leaderboard.Enabled);
        setShowCompetitorID(leaderboard.GroupBy === 'Individual' ? (leaderboard.ShowCompetitorID ?? true) : false);
        setShowCompetitorName(leaderboard.GroupBy === 'Individual' ? (leaderboard.ShowCompetitorName ?? true) : false);
        setShowOrganization(leaderboard.GroupBy === 'Individual' ? (leaderboard.ShowOrganization ?? true) : true);
        setShowCompetitorState(leaderboard.GroupBy === 'Individual' ? (leaderboard.ShowCompetitorState ?? true) : false);
        setShowDivision(leaderboard.GroupBy === 'Individual' ? (leaderboard.ShowDivision ?? true) : false);
        setShowRollupPoints(leaderboard.ShowRollupPoints ?? true);
        setShowCompetitionRanks(leaderboard.ShowCompetitionRanks ?? false);
    
        // Map TieBreakingCompetitionName to CompetitionID
        if (leaderboard.TieBreakingCompetitionName) {
            const tieBreakerCompetition = competitions.find(
                (c) => c.CompetitionName === leaderboard.TieBreakingCompetitionName
            );
            setTieBreaker(tieBreakerCompetition ? tieBreakerCompetition.CompetitionID : '');
        } else {
            setTieBreaker('');
        }
    
        // Fetch competition links for this leaderboard
        try {
            const response = await fetch(`${API_BASE_URL}/getAllRollupLeaderboards?EventID=${selectedEventID}&RollupLeaderboardID=${leaderboard.RollupLeaderboardID}`);
            if (response.ok) {
                const data = await response.json();
                if (data && data.length > 0 && data[0].CompetitionLinks) {
                    // Parse the competition links JSON if needed
                    const competitionLinks = typeof data[0].CompetitionLinks === 'string'
                        ? JSON.parse(data[0].CompetitionLinks)
                        : data[0].CompetitionLinks;
    
                    // Format the competition IDs as expected by the form
                    const formattedCompetitionLinks = competitionLinks.map(link => ({
                        competitionID: link.CompetitionID,
                        weight: link.CompetitionWeight || 100,
                    }));
    
                    setSelectedCompetitions(formattedCompetitionLinks);
                    return; // Exit early if competition links are successfully fetched
                }
            }
        } catch (error) {
            console.error('Error fetching competition links:', error);
        }
    
        // Fallback: Parse AssociatedCompetitions if competition links are not available
        if (leaderboard.AssociatedCompetitions && competitions.length > 0) {
            const competitionLinks = leaderboard.AssociatedCompetitions.split(', ').map(entry => {
                const match = entry.match(/^(.*) \(Weight: (\d+)\)$/);
                if (match) {
                    const name = match[1].trim();
                    const weight = parseInt(match[2], 10);
                    return { name, weight };
                }
                return null;
            }).filter(Boolean); // Remove invalid entries
    
            const links = competitionLinks
                .map(({ name, weight }) => {
                    const comp = competitions.find(c => c.CompetitionName === name);
                    return comp ? { competitionID: comp.CompetitionID, weight } : null;
                })
                .filter(Boolean); // Remove null values
    
            setSelectedCompetitions(links);
        }
        openModal();
    };

    // Handle cancel edit
    const handleCancelEdit = () => {
        setEditing(false);
        setCurrentLeaderboardID(null);
        setLeaderboardName('');
        setSelectedCompetitions([]);
        setTieBreaker('');
        setGroupedBy('Individual');
        setTopNCompetitors('9999');
        setWeightingMethod('points');
        setRollupMethod('Rank');
        setRefreshInterval(120);
        setIsEnabled(true);
        applyGroupVisibilityDefaults('Individual');
        setShowCompetitionRanks(false);
    };

    return (
        <div style={{ padding: '1rem' }}>
            {/* Event Selection */}
            <div style={{ marginBottom: '1rem' }}>
                <label>
                    Event:
                    <select 
                        value={selectedEventID} 
                        onChange={handleEventChange}
                        style={{ marginLeft: '0.5rem' }}
                    >
                        <option value="">Select Event</option>
                        {events.map(event => (
                            <option key={event.EventID} value={event.EventID}>{event.EventName}</option>
                        ))}
                    </select>
                </label>
            </div>

            <button className="btn btn-primary mb-1" onClick={openModal}>Add Rollup Leaderboard</button>

            {/* Existing Rollup Leaderboards Table */}
            <div style={{ marginTop: '2rem' }} className="data-table-wrapper">
                {rollupLeaderboards.length > 0 ? (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Status</th>
                                <th>Group By</th>
                                <th>Top N</th>
                                <th>Weighting Method</th>
                                <th>Rollup Method</th>
                                <th>Refresh Interval</th>
                                <th>Tie Breaker Competition</th>
                                <th>Associated Competitions</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rollupLeaderboards.map(lb => (
                                <tr key={lb.RollupLeaderboardID}>
                                    <td>{lb.RollupLeaderboardName}</td>
                                    <td>{lb.Enabled ? 'Visible' : 'Hidden'}</td>
                                    <td>{lb.GroupBy}</td>
                                    <td>{lb.TopN === 9999 ? 'All' : lb.TopN}</td>
                                    <td>{lb.WeightingMethod}</td>
                                    <td>{lb.RollupMethod}</td>
                                    <td>{lb.RefreshInterval || 'N/A'} sec</td>
                                    <td>{lb.TieBreakingCompetitionName || 'N/A'}</td>
                                    <td style={{ whiteSpace: 'pre-line' }}>
                                        {lb.AssociatedCompetitions?.split(', ').join('\n') || 'N/A'}
                                    </td>
                                    <td>
                                        <button
                                            onClick={() => handleEdit(lb)} 
                                            className="btn btn-primary"
                                            style={{ marginRight: '4px' }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleDelete(lb.RollupLeaderboardID)} 
                                            className="btn btn-danger"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p>No rollup leaderboards found for the selected event.</p>
                )}
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal">
                        <h3>{editing ? 'Edit Rollup Leaderboard' : 'Add Rollup Leaderboard'}</h3>
                        
                        {/* Rollup Leaderboard Name */}
                        <div style={{ marginBottom: '1rem' }}>
                            <label>
                                Rollup Leaderboard Name:
                                <input
                                    type="text"
                                    value={leaderboardName}
                                    onChange={(e) => setLeaderboardName(e.target.value)}
                                    placeholder="Enter leaderboard name"
                                    style={{ marginLeft: '0.5rem', padding: '0.5rem', width: '60%' }}
                                />
                            </label>
                        </div>

                        {/* Enabled/Disabled Status */}
                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                    type="checkbox"
                                    checked={isEnabled}
                                    onChange={(e) => setIsEnabled(e.target.checked)}
                                />
                                Visible to the public
                                <button
                                    type="button"
                                    className="help-icon"
                                    onClick={() => toggleHelp('visibility')}
                                    aria-label="Visibility help"
                                >
                                    ?
                                </button>
                                {openHelpId === 'visibility' && (
                                    <span className="help-text">
                                        Publicly visible with other leaderboards, or hidden from the public and visible only to admins.
                                    </span>
                                )}
                            </label>
                        </div>

                        {/* Competition Selection */}
                        <div style={{ marginBottom: '1rem', border: '1px solid #ccc', padding: '1rem' }}>
                            <h3>Competitions to Include in rollup leaderboard:</h3>
                            {competitions.map(competition => {
                                const selected = selectedCompetitions.find(c => c.competitionID === competition.CompetitionID);
                                return (
                                    <div key={competition.CompetitionID} style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <input 
                                                type="checkbox" 
                                                checked={!!selected}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        handleCompetitionChange(competition.CompetitionID, selected?.weight || 100);
                                                    } else {
                                                        setSelectedCompetitions(prev => prev.filter(c => c.competitionID !== competition.CompetitionID));
                                                    }
                                                }}
                                            />
                                            {competition.CompetitionName}
                                            {selected && (
                                                <>
                                                    <span>Competition Weight:</span>
                                                    <button
                                                        type="button"
                                                        className="help-icon"
                                                        onClick={() => toggleHelp(`competitionWeight-${competition.CompetitionID}`)}
                                                        aria-label="Competition weight help"
                                                    >
                                                        ?
                                                    </button>
                                                    {openHelpId === `competitionWeight-${competition.CompetitionID}` && (
                                                        <span className="help-text">
                                                            The weight is the starting rollup points for 1st place and also the maximum number of competitors who can earn rollup points in that competition. Lower weights reduce influence. 
                                                            Ignored when Popular weighting or "Average" rollup method are selected.
                                                        </span>
                                                    )}
                                                    <input
                                                        type="number"
                                                        value={selected.weight}
                                                        onChange={(e) => handleCompetitionChange(competition.CompetitionID, parseInt(e.target.value, 10))}
                                                        style={{ width: '60px' }}
                                                        min="1"
                                                    />
                                                </>
                                            )}
                                        </label>
                                    </div>
                                );
                            })}
                            {competitions.length === 0 && <p>No competitions available for this event</p>}
                        </div>

                        {/* Grouped By */}
                        <div style={{ marginBottom: '1rem' }}>
                            <label>
                                Rollup Leaderboard Grouped By:
                                <select 
                                    value={groupedBy} 
                                    onChange={handleGroupingChange}
                                    style={{ marginLeft: '0.5rem' }}
                                >
                                    {groupingOptions.map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="help-icon"
                                    onClick={() => toggleHelp('groupBy')}
                                    aria-label="Group by help"
                                >
                                    ?
                                </button>
                                {openHelpId === 'groupBy' && (
                                    <span className="help-text">
                                        Sets whether the rollup leaderboard applies to individual competitors or organizations.
                                        Note: For Team-based competitions in Organization grouped rollup leaderboards, rollup points are divide amongst team members to prevent inflated org scores. Individual rollups give full points to each team member.
                                    </span>
                                )}
                            </label>
                        </div>

                        {/* Top N Competitors */}
                        <div style={{ marginBottom: '1rem' }}>
                            <label>
                                Top Ranked Competitors Included - Per Organization:
                                <select 
                                    value={topNCompetitors} 
                                    onChange={(e) => setTopNCompetitors(parseInt(e.target.value, 10))}
                                    disabled={groupedBy === 'Individual'}
                                    style={{ marginLeft: '0.5rem' }}
                                >
                                    {topNOptions.map(option => (
                                        <option key={option} value={option}>{option === 9999 ? 'All' : option}</option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    className="help-icon"
                                    onClick={() => toggleHelp('topN')}
                                    aria-label="Top N help"
                                >
                                    ?
                                </button>
                                {openHelpId === 'topN' && (
                                    <span className="help-text">
                                        Limits rollup eligibility to the top N ranked competitors within each organization. 
                                        Note: Tied competitors receive equal rollup points.
                                    </span>
                                )}
                            </label>
                        </div>

                        {/* Weighting Options */}
                        <div style={{ marginBottom: '1rem', border: '1px solid #ccc', padding: '1rem' }}>
                            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                Competition Weighting Options
                                <button
                                    type="button"
                                    className="help-icon"
                                    onClick={() => toggleHelp('weightingMethod')}
                                    aria-label="Weighting method help"
                                >
                                    ?
                                </button>
                                {openHelpId === 'weightingMethod' && (
                                    <span className="help-text">
                                        Direct weighted uses manually assigned competition weights. Popular weighting uses the number of competitors, so events with more participation count more, and manually assigned weights are ignored.
                                    </span>
                                )}
                            </h3>
                            <div>
                                <label>
                                    <input 
                                        type="radio" 
                                        name="weightingMethod" 
                                        value="Weighted" 
                                        checked={weightingMethod === 'Weighted'} 
                                        onChange={(e) => setWeightingMethod(e.target.value)}
                                    />
                                    Direct Weighted: Prioritizes participation in more competitions
                                </label>
                            </div>
                            <div style={{ marginTop: '0.5rem' }}>
                                <label>
                                    <input 
                                        type="radio" 
                                        name="weightingMethod" 
                                        value="Popular" 
                                        checked={weightingMethod === 'Popular'} 
                                        onChange={(e) => setWeightingMethod(e.target.value)}
                                />
                                    Popular Weighting: Prioritizes competitions which have more participation
                                </label>
                            </div>
                        </div>

                        {/* Rollup Method */}
                        <div style={{ marginBottom: '1rem' }}>
                            <label>
                                Rollup Method:
                                <select
                                    value={rollupMethod}
                                    onChange={(e) => setRollupMethod(e.target.value)}
                                    style={{ marginLeft: '0.5rem' }}
                                >
                                    <option value="Rank">Rank (Recommended)</option>
                                    <option value="Average" disabled={hasMixedScoreDirections}>Average</option>
                                </select>
                                <button
                                    type="button"
                                    className="help-icon"
                                    onClick={() => toggleHelp('rollupMethod')}
                                    aria-label="Rollup method help"
                                >
                                    ?
                                </button>
                                {openHelpId === 'rollupMethod' && (
                                    <span className="help-text">
                                        Rank uses placement / rankings across included competitions. Average uses average scores across included competitions.
                                        Rank is recommended for most use cases since it normalizes competitions with different scoring methods. 
                                        Average can be used when all included competitions have the same scoring methods and you want to reward best absolute scores vs best rankings accross competitions. 
                                        Note: When Average is selected only competitors / organizations that have scores in all included competitions will be ranked.
                                    </span>
                                )}
                            </label>
                            {hasMixedScoreDirections && (
                                <div style={{ marginTop: '0.5rem', color: '#8a0f0f' }}>
                                    Average method is disabled when selected competitions mix high-score and low-score winning competitions.
                                </div>
                            )}
                        </div>

                        {/* Refresh Interval */}
                        <div style={{ marginBottom: '1rem' }}>
                            <label>
                                Refresh Interval (Seconds):
                                <select
                                    value={refreshInterval}
                                    onChange={(e) => setRefreshInterval(parseInt(e.target.value, 10))}
                                    style={{ marginLeft: '0.5rem' }}
                                >
                                    <option value={15}>15</option>
                                    <option value={30}>30</option>
                                    <option value={60}>60</option>
                                    <option value={120}>120</option>
                                    <option value={240}>240</option>
                                    <option value={480}>480</option>
                                    <option value={960}>960</option>
                                </select>
                                <button
                                    type="button"
                                    className="help-icon"
                                    onClick={() => toggleHelp('refreshInterval')}
                                    aria-label="Refresh interval help"
                                >
                                    ?
                                </button>
                                {openHelpId === 'refreshInterval' && (
                                    <span className="help-text">
                                        Controls how frequently the leaderboard refreshes data.
                                    </span>
                                )}
                            </label>
                        </div>

                        {/* Column Visibility Settings */}
                        <div style={{ marginBottom: '1rem', border: '1px solid #ccc', padding: '1rem' }}>
                            <h3>Column Visibility Settings</h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.5rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="checkbox"
                                        checked={showCompetitorID}
                                        onChange={(e) => setShowCompetitorID(e.target.checked)}
                                        disabled={groupedBy !== 'Individual'}
                                    />
                                    Show Competitor ID
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="checkbox"
                                        checked={showCompetitorName}
                                        onChange={(e) => setShowCompetitorName(e.target.checked)}
                                        disabled={groupedBy !== 'Individual'}
                                    />
                                    Show Competitor Name
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="checkbox"
                                        checked={showOrganization}
                                        onChange={(e) => setShowOrganization(e.target.checked)}
                                        disabled={groupedBy !== 'Individual'}
                                    />
                                    Show Organization
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="checkbox"
                                        checked={showCompetitorState}
                                        onChange={(e) => setShowCompetitorState(e.target.checked)}
                                        disabled={groupedBy !== 'Individual'}
                                    />
                                    Show Competitor State
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="checkbox"
                                        checked={showDivision}
                                        onChange={(e) => setShowDivision(e.target.checked)}
                                        disabled={groupedBy !== 'Individual'}
                                    />
                                    Show Division
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="checkbox"
                                        checked={showRollupPoints}
                                        onChange={(e) => setShowRollupPoints(e.target.checked)}
                                    />
                                    Show Rollup Points / Average Score
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <input
                                        type="checkbox"
                                        checked={showCompetitionRanks}
                                        onChange={(e) => setShowCompetitionRanks(e.target.checked)}
                                    />
                                    Show Rank In Competitions
                                </label>
                            </div>
                        </div>

                        {/* Tie Breaker */}
                        <div style={{ marginBottom: '1rem', border: '1px solid #ccc', padding: '1rem' }}>
                            <h3>Tie Breaker Competition</h3>
                            <div style={{ marginTop: '0.5rem' }}>
                                <label>
                                    Placement in:
                                    <select 
                                        value={tieBreaker} 
                                        onChange={(e) => setTieBreaker(e.target.value)}
                                        style={{ marginLeft: '0.5rem', width: '60%' }}
                                    >
                                        <option value="">Select Competition</option>
                                        {selectedCompetitions.map(selected => {
                                            const competition = competitions.find(c => c.CompetitionID === selected.competitionID);
                                            return competition ? (
                                                <option key={competition.CompetitionID} value={competition.CompetitionID}>
                                                    {competition.CompetitionName}
                                                </option>
                                            ) : null;
                                        })}
                                    </select>
                                </label>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleSave}>
                                {editing ? 'Update' : 'Save'} Rollup Leaderboard
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default RollupLeaderboardManagement;
